import type { Dayjs } from 'dayjs'

import type { MaterialPlatform, MaterialStatus } from './types'

import { dayjs } from '#/utils/dayjs'

import { AlgoAnalysisStatusEnum, MATERIAL_CATEGORIES } from './types'

export const FILTER_ALL_VALUE = '全部'

export const MATERIAL_FILTER_TABS = {
  CATEGORY_ANALYSIS: 'category-analysis',
  ANALYZED: 'analyzed',
  LIBRARY: 'library',
  STORYBOARD_LIBRARY: 'storyboard-library',
} as const

export const MATERIAL_FILTER_TAB_LABEL_MAP: Record<
  (typeof MATERIAL_FILTER_TABS)[keyof typeof MATERIAL_FILTER_TABS],
  string
> = {
  [MATERIAL_FILTER_TABS.CATEGORY_ANALYSIS]: '品类分析',
  [MATERIAL_FILTER_TABS.ANALYZED]: '已分析',
  [MATERIAL_FILTER_TABS.LIBRARY]: '素材库',
  [MATERIAL_FILTER_TABS.STORYBOARD_LIBRARY]: '分镜库',
}

export const MATERIAL_FILTER_PLATFORM_VALUES = {
  FACEBOOK: 'facebook',
  TIKTOK: 'TikTok',
} as const

export const MATERIAL_UPLOAD_PLATFORM_VALUES = {
  FACEBOOK: 'facebook',
  TIKTOK: 'tiktok',
} as const

export const MATERIAL_PLATFORM_LABELS = {
  META: 'Meta',
  TIKTOK: 'TikTok',
} as const

export const MATERIAL_STATUS_LABELS = {
  ANALYZED: '已分析',
  IN_PROCESS: '分析中',
  PENDING: '未分析',
} as const satisfies Record<string, MaterialStatus>

export const MATERIAL_SORT_VALUES = {
  ACTIVE_ADS: 'Active Ads ↓',
  CTR: 'CTR ↓',
  CVR: 'CVR ↓',
  ROAS: 'Roas ↓',
  SPEND: 'Spend ↓',
  TOTAL_ADS: 'Total Ads ↓',
} as const

export const MATERIAL_DATE_RANGE_MAX_DAYS = 90

export const getMaterialYesterdayStart = () =>
  dayjs().subtract(1, 'day').startOf('day')

export const getMaterialYesterdayEnd = () =>
  dayjs().subtract(1, 'day').endOf('day')

export const buildMaterialDefaultDateRange = (): [Dayjs, Dayjs] => [
  getMaterialYesterdayStart().subtract(6, 'day').startOf('day'),
  getMaterialYesterdayEnd(),
]

export const buildMaterialDatePresets = () =>
  [
    {
      label: 'Yesterday',
      value: [getMaterialYesterdayStart(), getMaterialYesterdayEnd()],
    },
    {
      label: 'Last 3 Days',
      value: [
        getMaterialYesterdayStart().subtract(2, 'day').startOf('day'),
        getMaterialYesterdayEnd(),
      ],
    },
    {
      label: 'Last 7 Days',
      value: [
        getMaterialYesterdayStart().subtract(6, 'day').startOf('day'),
        getMaterialYesterdayEnd(),
      ],
    },
    {
      label: 'Last 30 Days',
      value: [
        getMaterialYesterdayStart().subtract(29, 'day').startOf('day'),
        getMaterialYesterdayEnd(),
      ],
    },
    {
      label: 'Last 60 Days',
      value: [
        getMaterialYesterdayStart().subtract(59, 'day').startOf('day'),
        getMaterialYesterdayEnd(),
      ],
    },
    {
      label: 'Last 90 Days',
      value: [
        getMaterialYesterdayStart().subtract(89, 'day').startOf('day'),
        getMaterialYesterdayEnd(),
      ],
    },
  ] satisfies Array<{ label: string; value: [Dayjs, Dayjs] }>

export const MATERIAL_CHART_METRICS = {
  SPEND: 'Spend',
  ROAS: 'Roas',
} as const

export const MATERIAL_DETAIL_TABS = {
  ELEMENT: '元素',
  OTHER: '其他',
  PERFORMANCE: '表现',
  STORYBOARD: '分镜',
  VOICE: '语音',
} as const

export const MATERIAL_TEXT_REWRITE_STATUS = {
  FAILURE: 'failure',
  IN_PROCESS: 'in_process',
  SUCCESS: 'success',
} as const

export const MATERIAL_DETAIL_TAB_OPTIONS = [
  MATERIAL_DETAIL_TABS.PERFORMANCE,
  MATERIAL_DETAIL_TABS.ELEMENT,
  MATERIAL_DETAIL_TABS.VOICE,
  MATERIAL_DETAIL_TABS.STORYBOARD,
  MATERIAL_DETAIL_TABS.OTHER,
] as const

export const MATERIAL_ELEMENT_SECTION_TITLES = {
  core_selling_points: '核心卖点',
  marketing_rhythms: '营销细节',
  trust_endorsements: '信任背书',
  user_pain_points: '用户痛点',
} as const

export const MATERIAL_ELEMENT_SECTION_GROUP_TITLES = {
  CTA: '行动号召 (CTA)',
  EFFICACY_CLAIMS: '功效承诺',
  SOCIAL_PROOF: '社会证明',
  TRUST_SIGNALS: '信任信号',
  URGENCY_DETAILS: '紧迫感诱因 (详细)',
} as const

export const MATERIAL_ELEMENT_SECTION_DEFAULT_OPEN = {
  core_selling_points: true,
  marketing_rhythms: true,
  trust_endorsements: true,
  user_pain_points: true,
} as const

export const MATERIAL_OTHER_SECTION_KEYS = {
  ENVIRONMENT_LIGHTING: 'environment_lighting',
  SOUND_EFFECTS: 'sound_effects',
  SPECIAL_EFFECTS: 'special_effects',
  VISUAL_SUBJECT: 'visual_subject',
  VOICE_AND_MUSIC: 'voice_and_music',
} as const

export const MATERIAL_OTHER_SECTION_DEFAULT_OPEN = {
  [MATERIAL_OTHER_SECTION_KEYS.VISUAL_SUBJECT]: true,
  [MATERIAL_OTHER_SECTION_KEYS.ENVIRONMENT_LIGHTING]: true,
  [MATERIAL_OTHER_SECTION_KEYS.SPECIAL_EFFECTS]: true,
  [MATERIAL_OTHER_SECTION_KEYS.VOICE_AND_MUSIC]: true,
  [MATERIAL_OTHER_SECTION_KEYS.SOUND_EFFECTS]: true,
} as const

export const MATERIAL_OTHER_SECTION_TITLES = {
  AUDIO_INFO: '听觉信息',
  AUDIO_LABEL: '语音类型',
  AVG_SHOT_DURATION: '平均场景时长',
  DURATION: '时长',
  ENVIRONMENT_LIGHTING: '环境与光线',
  ENVIRONMENT_LINE: '环境',
  LIGHTING_LINE: '光线',
  MUSIC_LINE: '音乐',
  SCENE_COUNT: '场景数',
  SOUND_EFFECTS: '音效',
  SPECIAL_EFFECTS: '特殊效果',
  SPEECH_RATE_LINE: '语速',
  VIDEO_DESCRIPTION: '视频描述',
  VISUAL_INFO: '视觉信息',
  VISUAL_RHYTHM_LINE: '节奏',
  VISUAL_SUBJECT: '视觉主体',
  VISUAL_STYLE_LINE: '风格',
  VOICE_AND_MUSIC: '语音与音乐',
} as const

export const MATERIAL_DETAIL_TEXT = {
  ELEMENT_EMPTY: '暂无数据',
  ELEMENT_META_EMPTY: '-',
  MANUAL_EMPTY:
    '该素材还没进行分析，没有分析结果。如需查看分析结果，请先开始分析该素材。',
  MANUAL_EMPTY_FAILURE:
    '该素材分析失败，没有分析结果。如需查看分析结果，请重新分析该素材。',
  MANUAL_EMPTY_ANALYZING: '已开始分析素材，请稍后查看分析结果',
  MANUAL_METRICS_ONLY_TIP:
    '该素材为手动上传素材，其数据表现如下：（仅展示上传时填写的值）',
  NO_CHART_DATA_PREFIX: '暂无',
  NO_ELEMENT_ANALYSIS_RESULT: '暂无元素分析结果',
  NO_STORYBOARD_ANALYSIS_RESULT: '暂无分镜分析结果',
  PLACEHOLDER_SUFFIX: '内容待补充',
} as const

export const MATERIAL_UPLOAD_TEXT = {
  BASIC_INFO: '基本信息',
  CATEGORY_LABEL: '品类',
  CATEGORY_PLACEHOLDER: '请选择品类',
  DOMAIN_LABEL: '站点',
  DOMAIN_PLACEHOLDER: '输入店铺域名',
  FILE_DUPLICATE_ERROR: '视频文件已存在，请勿重复上传',
  FILE_INVALID_ERROR: '文件校验失败，请重试',
  FILE_LABEL: '视频文件',
  FILE_PLACEHOLDER: '请选择视频文件',
  FILE_SIZE_ERROR: '文件限制 500M，请压缩后上传',
  METRIC_PLACEHOLDER: '输入数值',
  METRICS: '表现指标',
  PLATFORM_LABEL: '平台',
  PLATFORM_PLACEHOLDER: '请选择平台',
  SUBMIT_SUCCESS: '上传成功',
  UPLOAD_CONFIRM: '确定',
  UPLOAD_TITLE: '上传素材',
} as const

export const MATERIAL_UPLOAD_CONFIG = {
  // SR 版素材上传需要进入新版素材分析业务域，避免继续写入 MySQL 旧版素材池。
  BIZ_CODE: 'material_analysis_sr',
  MAX_SIZE: 500 * 1024 * 1024,
  UPLOAD_CONCURRENT_CHUNKS: 7,
} as const

export const MATERIAL_UPLOAD_ACCEPT_VIDEO_TYPES = '.mp4,.mov,.webm'

export const MATERIAL_UPLOAD_ALLOWED_EXTENSIONS = new Set([
  'mov',
  'mp4',
  'webm',
])

/**
 * 品类下拉统一按展示文案进行前端过滤。
 * 这样所有入口都能复用同一套搜索规则，避免因为实现分散导致匹配行为不一致。
 */
export const filterMaterialCategoryOption = (
  input: string,
  option?: { label?: unknown },
) =>
  String(option?.label || '')
    .toLowerCase()
    .includes(input.toLowerCase())

const MATERIAL_CATEGORY_VALUES = [...new Set(MATERIAL_CATEGORIES)]

export const MATERIAL_FILTER_CATEGORY_OPTIONS = [
  { label: '所有品类', value: FILTER_ALL_VALUE },
  ...MATERIAL_CATEGORY_VALUES.map((category) => ({
    label: category,
    value: category,
  })),
]

export const MATERIAL_UPLOAD_CATEGORY_OPTIONS = MATERIAL_CATEGORY_VALUES.map(
  (category) => ({
    label: category,
    value: category,
  }),
)

export const MATERIAL_FILTER_PLATFORM_OPTIONS = [
  { label: '所有平台', value: FILTER_ALL_VALUE },
  {
    label: MATERIAL_PLATFORM_LABELS.META,
    value: MATERIAL_FILTER_PLATFORM_VALUES.FACEBOOK,
  },
  {
    label: MATERIAL_PLATFORM_LABELS.TIKTOK,
    value: MATERIAL_FILTER_PLATFORM_VALUES.TIKTOK,
  },
]

export const MATERIAL_UPLOAD_PLATFORM_OPTIONS = [
  {
    label: MATERIAL_PLATFORM_LABELS.META,
    value: MATERIAL_UPLOAD_PLATFORM_VALUES.FACEBOOK,
  },
  {
    label: MATERIAL_PLATFORM_LABELS.TIKTOK,
    value: MATERIAL_UPLOAD_PLATFORM_VALUES.TIKTOK,
  },
]

export const MATERIAL_STATUS_OPTIONS = [
  { label: '所有状态', value: FILTER_ALL_VALUE },
  {
    label: MATERIAL_STATUS_LABELS.PENDING,
    value: MATERIAL_STATUS_LABELS.PENDING,
  },
  {
    label: MATERIAL_STATUS_LABELS.IN_PROCESS,
    value: MATERIAL_STATUS_LABELS.IN_PROCESS,
  },
  {
    label: MATERIAL_STATUS_LABELS.ANALYZED,
    value: MATERIAL_STATUS_LABELS.ANALYZED,
  },
]

export const MATERIAL_SORT_OPTIONS = [
  {
    label: MATERIAL_SORT_VALUES.ROAS,
    value: MATERIAL_SORT_VALUES.ROAS,
  },
  {
    label: MATERIAL_SORT_VALUES.CTR,
    value: MATERIAL_SORT_VALUES.CTR,
  },
  {
    label: MATERIAL_SORT_VALUES.CVR,
    value: MATERIAL_SORT_VALUES.CVR,
  },
  // {
  //   label: MATERIAL_SORT_VALUES.ACTIVE_ADS,
  //   value: MATERIAL_SORT_VALUES.ACTIVE_ADS,
  // },
  // {
  //   label: MATERIAL_SORT_VALUES.TOTAL_ADS,
  //   value: MATERIAL_SORT_VALUES.TOTAL_ADS,
  // },
  {
    label: MATERIAL_SORT_VALUES.SPEND,
    value: MATERIAL_SORT_VALUES.SPEND,
  },
]

export const MATERIAL_STATUS_TO_ALGO_ANALYSIS_STATUS_MAP: Record<
  MaterialStatus,
  AlgoAnalysisStatusEnum[]
> = {
  [MATERIAL_STATUS_LABELS.PENDING]: [AlgoAnalysisStatusEnum.PENDING],
  [MATERIAL_STATUS_LABELS.IN_PROCESS]: [AlgoAnalysisStatusEnum.IN_PROCESS],
  [MATERIAL_STATUS_LABELS.ANALYZED]: [
    AlgoAnalysisStatusEnum.SUCCESS,
    AlgoAnalysisStatusEnum.FAILURE,
  ],
}

export const MATERIAL_ALGO_ANALYSIS_STATUS_TO_STATUS_MAP: Record<
  AlgoAnalysisStatusEnum,
  MaterialStatus
> = {
  [AlgoAnalysisStatusEnum.PENDING]: MATERIAL_STATUS_LABELS.PENDING,
  [AlgoAnalysisStatusEnum.IN_PROCESS]: MATERIAL_STATUS_LABELS.IN_PROCESS,
  [AlgoAnalysisStatusEnum.SUCCESS]: MATERIAL_STATUS_LABELS.ANALYZED,
  [AlgoAnalysisStatusEnum.FAILURE]: MATERIAL_STATUS_LABELS.ANALYZED,
}

export const MATERIAL_FILTER_PLATFORM_QUERY_MAP = {
  [MATERIAL_FILTER_PLATFORM_VALUES.FACEBOOK]:
    MATERIAL_FILTER_PLATFORM_VALUES.FACEBOOK,
  [MATERIAL_FILTER_PLATFORM_VALUES.TIKTOK]:
    MATERIAL_UPLOAD_PLATFORM_VALUES.TIKTOK,
} as const

export const MATERIAL_UPLOAD_PLATFORM_LABEL_MAP: Record<
  (typeof MATERIAL_UPLOAD_PLATFORM_VALUES)[keyof typeof MATERIAL_UPLOAD_PLATFORM_VALUES],
  MaterialPlatform
> = {
  [MATERIAL_UPLOAD_PLATFORM_VALUES.FACEBOOK]: MATERIAL_PLATFORM_LABELS.META,
  [MATERIAL_UPLOAD_PLATFORM_VALUES.TIKTOK]: MATERIAL_PLATFORM_LABELS.TIKTOK,
}
